import { Button, Select, TextInput, UnstyledButton } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconCalendarDue,
  IconClipboardCheck,
  IconHistory,
  IconPrinter,
  IconSearch,
  IconShieldCheck,
  IconSignature,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../EmptyState.js';
import { formatDateTime, formatDay } from '../../../format.js';
import { offsetLabel } from '../../../inputs/lt-utc.js';
import { PageHeader } from '../../../PageHeader.js';
import { Panel } from '../../../Panel.js';
import { StatTile } from '../../../StatTile.js';
import { StatusBadge } from '../../../StatusBadge.js';
import { useShell } from '../../../shell-context.js';
import documents from '../../documents/Documents.module.css';
import table from '../../materials/Materials.module.css';
import { browserPdfOutput, pdfErrorKey, renderPdf } from '../../personnel/pdf.js';
import { errorCode } from '../../personnel/source.js';
import purchasing from '../../purchasing/Purchasing.module.css';
import { LoadError, NoMatch, TablePager, TableSkeleton } from '../../purchasing/table.js';
import { type BuilderTab, RaBuilder } from '../library/RaBuilder.js';
import library from '../library/RaLibrary.module.css';
import { VersionList } from '../library/RaPublication.js';
import type { RaVersionView } from '../library/types.js';
import master from '../master/RaMaster.module.css';
import { raPdfFileName, raVersionPdf } from '../pdf.js';
import { ActionDialog, LevelBadge } from '../RiskParts.js';
import type { RiskStatus } from '../types.js';
import { statusTone } from '../view.js';
import classes from './RaRegister.module.css';
import type {
  RaRegisterBell,
  RaRegisterSource,
  RaRegisterVersion,
  RaRegisterView,
  RaVesselRegister,
} from './types.js';
import {
  EMPTY_REGISTER_FILTER,
  filterRegister,
  REGISTER_STATES,
  type RegisterFilter,
} from './view.js';

const PAGE_SIZE = 15;

type Notice = { tone: 'success' | 'danger'; text: string };

export interface RaRegisterScreenProps {
  source: RaRegisterSource;
  /** Okundu onayından sonra (gemide zil ve pano tazelenir). */
  onChanged?: () => void;
}

/** Hata kodunun metni: register metinleri, yoksa risk metinleri. */
function useRegisterError() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = typeof error === 'string' ? error : errorCode(error);
    return (
      t(`riskRegister.errors.${code}`, { defaultValue: '' }) ||
      t(`risk.errors.${code}`, { defaultValue: '' }) ||
      `${t('riskRegister.errors.GENERIC')} (${code})`
    );
  };
}

const ACK_TONE = {
  acknowledged: 'success',
  pending: 'warning',
  not_required: 'neutral',
} as const;

/**
 * Compliance ▸ Risk Assessment ▸ Vessel Risk Register (K4; ADR 0088). Gemiye atanmış güncel şirket
 * RA sürümleri: revizyon, yayım günü, atama dayanağı, hedef kalan risk, gözden geçirme, okundu
 * onayı ve geminin bu sürümden açtığı RA'lar; eski sürümde kalmış gemi RA'ları "yeni sürüm var"
 * işaretiyle ayrı tabloda. Onay bekleyen sürüm başta ve belirgindir (R16). Gemide kaptan ya da
 * yetkili zabit sürümü okuyup gemi adına onaylar; ofiste gemi seçilir, salt okunur. Tasarım yok;
 * tablo dili Library.
 */
export function RaRegisterScreen({ source, onChanged }: RaRegisterScreenProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskRegister.${key}`, options);
  const message = useRegisterError();
  // Panodan ve raporlardan gelen bağlantı: `?vessel=…&version=…` (ADR 0095 §1)
  const search = new URLSearchParams(useShell()?.search ?? '');
  const [linked, setLinked] = useState(() => search.get('version'));
  const [state, setState] = useState<
    { kind: 'loading' } | { kind: 'error'; code: string } | { kind: 'ready'; view: RaRegisterView }
  >({ kind: 'loading' });
  const [vesselId, setVesselId] = useState<string | undefined>(
    () => search.get('vessel') ?? undefined,
  );
  const [printing, setPrinting] = useState(false);
  const [opened, setOpened] = useState<RaVersionView | null>(null);
  const [tab, setTab] = useState<BuilderTab>('general');
  const [acknowledging, setAcknowledging] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', view: await source.load(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  const view = state.kind === 'ready' ? state.view : null;
  const register = view?.register ?? null;
  const row = opened ? register?.versions.find((v) => v.id === opened.id) : undefined;
  const canAcknowledge = source.acknowledge !== undefined && view?.can.acknowledge === true;

  const open = async (id: string) => {
    try {
      setNotice(null);
      setTab('general');
      setOpened(await source.version(id));
    } catch (error) {
      setNotice({ tone: 'danger', text: message(error) });
    }
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: bağlantı bir kez açılır
  useEffect(() => {
    if (!linked || !register) return;
    setLinked(null);
    if (register.versions.some((v) => v.id === linked)) void open(linked);
  }, [linked, register]);
  /** Sürümün PDF'i (ADR 0095 §2): içerik, matris sürümü, onay izi ve gemideki okundu onayı. */
  const print = async () => {
    if (!opened) return;
    setPrinting(true);
    try {
      const brand = (await source.brand?.().catch(() => null)) ?? null;
      const tr = (key: string, options?: Record<string, unknown>) => String(t(key, options ?? {}));
      const definition = raVersionPdf(tr, {
        version: opened,
        register: row ?? null,
        vessel: register ? { name: register.vessel.name } : null,
        brand,
        printedAt: new Date().toISOString(),
      });
      const output = source.pdf ?? browserPdfOutput();
      await output.print(
        raPdfFileName(
          `${opened.code} Rev ${opened.revision}`,
          t('riskDashboard.pdf.version.title'),
        ),
        () => renderPdf(definition, { print: true }),
      );
    } catch (error) {
      const key = pdfErrorKey(errorCode(error));
      setNotice({
        tone: 'danger',
        text: key === 'restHours.pdf.error' ? t('riskDashboard.pdf.error') : t(key),
      });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className={purchasing.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconClipboardCheck size={26} />}
      />
      <p className={master.note}>{source.scope === 'ship' ? k('shipNote') : k('officeNote')}</p>
      {notice ? (
        <p
          className={purchasing.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      {state.kind === 'error' ? (
        <Panel>
          <LoadError title={k('empty.error')} code={state.code} onRetry={() => void load()} />
        </Panel>
      ) : !view ? (
        <Panel flush aria-label={k('title')}>
          <TableSkeleton columns={9} label={k('title')} />
        </Panel>
      ) : !register ? (
        <Panel>
          <EmptyState title={k('empty.title')} description={k('empty.noVessel')} />
        </Panel>
      ) : opened ? (
        <Panel flush aria-label={`${opened.code} · ${opened.title}`}>
          <RegisterVersionHead
            version={opened}
            row={row}
            scope={source.scope}
            canAcknowledge={canAcknowledge}
            onBack={() => setOpened(null)}
            onAcknowledge={() => setAcknowledging(true)}
            onPdf={() => void print()}
            printing={printing}
          />
          <RaBuilder
            content={opened.content}
            matrix={opened.matrixDefinition}
            editable={false}
            code={opened.code}
            revision={opened.revision}
            tab={tab}
            onTab={setTab}
            publication={
              <section className={library.card} aria-label={t('riskLibrary.publication.versions')}>
                <div className={library.cardHead}>
                  <span>{t('riskLibrary.publication.versions')}</span>
                </div>
                <div className={library.cardBody}>
                  <VersionList versions={[opened]} />
                </div>
              </section>
            }
          />
        </Panel>
      ) : (
        <RegisterList
          register={register}
          vessels={view.vessels}
          scope={source.scope}
          canAcknowledge={canAcknowledge}
          onOpen={(id) => void open(id)}
          onVessel={setVesselId}
        />
      )}
      {opened && row && source.acknowledge ? (
        <ActionDialog
          opened={acknowledging}
          title={k('dialog.title', { code: row.code, revision: row.revision })}
          body={k('dialog.body')}
          reason="none"
          password
          reauthKind="ra_acknowledgement"
          confirmLabel={k('dialog.confirm')}
          messageOf={message}
          onClose={() => setAcknowledging(false)}
          onConfirm={async (input) => {
            try {
              const next = await (source.acknowledge as NonNullable<typeof source.acknowledge>)(
                row.id,
                row.contentHash,
                input.password || undefined,
              );
              setState({ kind: 'ready', view: next });
              setAcknowledging(false);
              setNotice({
                tone: 'success',
                text: k('notice.acknowledged', { code: row.code, revision: row.revision }),
              });
              onChanged?.();
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Açılan sürümün başlığı: salt okunur sürüm, onay durumu, onaylayan (ad, rütbe, gemi saati) ve
 * gemide "Acknowledge" düğmesi (yalnız kaptan ya da yetkili zabit).
 */
export function RegisterVersionHead({
  version,
  row,
  scope,
  canAcknowledge,
  onBack,
  onAcknowledge,
  onPdf,
  printing,
}: {
  version: RaVersionView;
  row: RaRegisterVersion | undefined;
  scope: 'ship' | 'web';
  canAcknowledge: boolean;
  onBack: () => void;
  onAcknowledge: () => void;
  /** Sürümün PDF'i (K7; ADR 0095 §2). */
  onPdf?: () => void;
  printing?: boolean;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskRegister.${key}`, options);
  return (
    <div className={library.head}>
      <div className={library.headTitle}>
        <Button
          variant="subtle"
          size="compact-sm"
          leftSection={<IconArrowLeft size={14} />}
          onClick={onBack}
          style={{ alignSelf: 'flex-start' }}
        >
          {k('detail.back')}
        </Button>
        <h2>
          {version.code} · {version.title}
        </h2>
        <span className={library.meta}>
          <span>{k('detail.readOnly', { revision: version.revision })}</span>
          {row ? (
            <StatusBadge tone={ACK_TONE[row.acknowledgement]} withIcon={false}>
              {k(`acknowledgement.${row.acknowledgement}`)}
            </StatusBadge>
          ) : null}
        </span>
      </div>
      {row ? (
        <div className={classes.ackBox}>
          <span className={classes.ackText}>
            {row.acknowledgedBy
              ? k('detail.acknowledged', {
                  name: row.acknowledgedBy.byName ?? k('none'),
                  role: row.acknowledgedBy.roleName ?? k('none'),
                  time: acknowledgedTime(row),
                })
              : row.acknowledgement === 'pending'
                ? scope === 'ship'
                  ? k('detail.pending')
                  : k('detail.pendingOffice')
                : k('detail.notRequired')}
          </span>
          {onPdf ? (
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              loading={printing === true}
              onClick={onPdf}
            >
              {t('riskDashboard.pdf.button')}
            </Button>
          ) : null}
          {canAcknowledge && !row.acknowledgedBy ? (
            <Button
              leftSection={<IconSignature size={16} />}
              variant={row.acknowledgement === 'pending' ? 'filled' : 'default'}
              onClick={onAcknowledge}
            >
              {k('detail.acknowledge')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Register'ın listesi: özet kartları, süzgeç, güncel sürümler tablosu ve eski sürümdeki gemi
 * RA'ları. Ofiste gemi seçimi.
 */
export function RegisterList({
  register,
  vessels,
  scope,
  canAcknowledge,
  onOpen,
  onVessel,
}: {
  register: RaVesselRegister;
  vessels?: { id: string; name: string }[] | undefined;
  scope: 'ship' | 'web';
  canAcknowledge: boolean;
  onOpen: (versionId: string) => void;
  onVessel?: (vesselId: string) => void;
}) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`riskRegister.${key}`, options);
  const [filter, setFilter] = useState<RegisterFilter>(EMPTY_REGISTER_FILTER);
  const [page, setPage] = useState(1);
  const items = useMemo(() => filterRegister(register.versions, filter), [register, filter]);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const basis = (v: RaRegisterVersion) =>
    v.basis === 'group'
      ? k('basis.group', { group: t(`riskMaster.groups.${register.vessel.raGroup ?? 'ALL'}`) })
      : k(`basis.${v.basis}`);
  return (
    <>
      <div className={master.stats}>
        <StatTile
          icon={<IconShieldCheck size={22} />}
          label={k('stats.current')}
          value={register.counts.current}
          variant="plain"
        />
        <StatTile
          icon={<IconSignature size={22} />}
          label={k('stats.pending')}
          value={register.counts.pending}
          tone={register.counts.pending > 0 ? 'warning' : 'default'}
          variant="plain"
        />
        <StatTile
          icon={<IconCalendarDue size={22} />}
          label={k('stats.overdue')}
          value={register.counts.overdue}
          tone={register.counts.overdue > 0 ? 'danger' : 'default'}
          variant="plain"
        />
        <StatTile
          icon={<IconHistory size={22} />}
          label={k('stats.outdated')}
          value={register.counts.outdated}
          tone={register.counts.outdated > 0 ? 'warning' : 'default'}
          variant="plain"
        />
      </div>
      <Panel flush aria-label={k('title')}>
        <div className={master.toolbar}>
          {vessels && onVessel ? (
            <Select
              className={master.filter}
              label={k('vessel')}
              data={vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={register.vessel.id}
              allowDeselect={false}
              onChange={(value) => {
                if (!value) return;
                onVessel(value);
                setPage(1);
              }}
            />
          ) : null}
          <TextInput
            className={master.search}
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={k('search')}
            aria-label={k('search')}
            value={filter.query}
            onChange={(event) => {
              setFilter({ ...filter, query: event.currentTarget.value });
              setPage(1);
            }}
          />
          <Select
            className={master.filter}
            label={k('filter.state')}
            data={REGISTER_STATES.map((s) => ({ value: s, label: k(`filter.states.${s}`) }))}
            value={filter.state}
            allowDeselect={false}
            onChange={(value) => {
              setFilter({ ...filter, state: (value ?? 'all') as RegisterFilter['state'] });
              setPage(1);
            }}
          />
        </div>
        {register.versions.length === 0 ? (
          <EmptyState
            title={k('empty.title')}
            description={scope === 'ship' ? k('empty.ship') : k('empty.office')}
          />
        ) : items.length === 0 ? (
          <NoMatch title={k('empty.noMatch')} onClear={() => setFilter(EMPTY_REGISTER_FILTER)} />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('tableLabel')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${purchasing.table}`}>
                <thead>
                  <tr>
                    <th scope="col">{k('columns.code')}</th>
                    <th scope="col">{k('columns.title')}</th>
                    <th scope="col">{k('columns.revision')}</th>
                    <th scope="col">{k('columns.published')}</th>
                    <th scope="col">{k('columns.basis')}</th>
                    <th scope="col">{k('columns.residual')}</th>
                    <th scope="col">{k('columns.review')}</th>
                    <th scope="col">{k('columns.acknowledgement')}</th>
                    <th scope="col">{k('columns.assessments')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((v) => (
                    <tr
                      key={v.id}
                      className={classes.row}
                      data-pending={v.acknowledgement === 'pending' || undefined}
                    >
                      <td>
                        <button
                          type="button"
                          className={master.codeButton}
                          aria-label={k('open', { code: v.code })}
                          onClick={() => onOpen(v.id)}
                        >
                          {v.code}
                        </button>
                        {v.migrated ? <span className={master.sub}>{k('migrated')}</span> : null}
                      </td>
                      <td className={master.wrapCell}>{v.title}</td>
                      <td>{v.revision}</td>
                      <td>{formatDay(v.publishedAt.slice(0, 10))}</td>
                      <td>{basis(v)}</td>
                      <td>
                        {v.residual ? (
                          <LevelBadge level={v.residual.level} score={v.residual.score} />
                        ) : (
                          k('none')
                        )}
                      </td>
                      <td>
                        <span className={classes.stack}>
                          <span>{formatDay(v.reviewDay)}</span>
                          {v.review === 'due' || v.review === 'overdue' ? (
                            <StatusBadge
                              tone={v.review === 'overdue' ? 'danger' : 'warning'}
                              withIcon={false}
                            >
                              {k(`review.${v.review}`)}
                            </StatusBadge>
                          ) : null}
                        </span>
                      </td>
                      <td>
                        <span className={classes.stack}>
                          <StatusBadge
                            tone={ACK_TONE[v.acknowledgement]}
                            withIcon={v.acknowledgement === 'pending'}
                          >
                            {k(`acknowledgement.${v.acknowledgement}`)}
                          </StatusBadge>
                          {v.acknowledgedBy ? (
                            <span className={master.sub}>
                              {k('acknowledgedBy', {
                                name: v.acknowledgedBy.byName ?? k('none'),
                                role: v.acknowledgedBy.roleName ?? k('none'),
                              })}
                              <br />
                              {acknowledgedTime(v)}
                            </span>
                          ) : v.acknowledgement === 'pending' && canAcknowledge ? (
                            <Button size="compact-xs" variant="light" onClick={() => onOpen(v.id)}>
                              {k('readAndAcknowledge')}
                            </Button>
                          ) : null}
                        </span>
                      </td>
                      <td>
                        {v.assessments.length === 0 ? (
                          k('none')
                        ) : (
                          <span className={classes.stack}>
                            {v.assessments.map((a) => (
                              <span key={a.id} className={classes.raNo}>
                                {a.raNo}
                              </span>
                            ))}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
      {register.outdated.length > 0 ? (
        <Panel flush title={k('outdated.title')}>
          <p className={classes.panelIntro}>{k('outdated.description')}</p>
          <section
            className={table.tableWrap}
            aria-label={k('outdated.tableLabel')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${table.table} ${purchasing.table}`}>
              <thead>
                <tr>
                  <th scope="col">{k('outdated.columns.raNo')}</th>
                  <th scope="col">{k('outdated.columns.title')}</th>
                  <th scope="col">{k('outdated.columns.status')}</th>
                  <th scope="col">{k('outdated.columns.from')}</th>
                  <th scope="col">{k('outdated.columns.latest')}</th>
                </tr>
              </thead>
              <tbody>
                {register.outdated.map((o) => (
                  <tr key={o.id}>
                    <td>{o.raNo}</td>
                    <td className={master.wrapCell}>{o.title}</td>
                    <td>
                      <StatusBadge tone={statusTone(o.status as RiskStatus)} withIcon={false}>
                        {t(`risk.statuses.${o.status}`)}
                      </StatusBadge>
                    </td>
                    <td>
                      {o.code} {o.sourceRevision}
                    </td>
                    <td>
                      <span className={classes.stack}>
                        <span>
                          {o.code} {o.latestRevision}
                        </span>
                        <StatusBadge tone="warning" withIcon>
                          {k('outdated.flag')}
                        </StatusBadge>
                        {o.latestAssigned ? null : (
                          <span className={master.sub}>{k('outdated.notAssigned')}</span>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </Panel>
      ) : null}
    </>
  );
}

/** Onay anı gemi saatiyle ve farkıyla (UTC asıl; R26). */
function acknowledgedTime(v: RaRegisterVersion): string {
  const ack = v.acknowledgedBy;
  if (!ack) return '';
  return `${formatDateTime(ack.at, ack.tzOffsetMin)} (${offsetLabel(ack.tzOffsetMin)})`;
}

/**
 * Zilin RA maddeleri: "N risk assessment versions awaiting acknowledgement" (R16; ADR 0088 §4)
 * Vessel Risk Register'ı, "N operations awaiting authorisation" (K7; ADR 0095 §5) Operational
 * Verification'ı açar. Panodaki uyarılarda da aynı maddeler.
 */
export function RaRegisterBellList({
  bell,
  onOpen,
  onOpenAuthorizations,
}: {
  bell: RaRegisterBell | null;
  onOpen: () => void;
  /** Yetki bekleyen doğrulamalar; verilmezse madde gösterilmez. */
  onOpenAuthorizations?: () => void;
}) {
  const { t } = useTranslation();
  const authorizations = onOpenAuthorizations ? (bell?.authorizations?.count ?? 0) : 0;
  if (!bell || (bell.count === 0 && authorizations === 0)) return null;
  return (
    <ul className={documents.bellList} aria-label={t('menu.complianceRisk')}>
      {bell.count > 0 ? (
        <li>
          <UnstyledButton className={documents.bellItem} data-counted onClick={onOpen}>
            <span className={documents.bellIcon} aria-hidden>
              <IconAlertTriangle size={16} />
            </span>
            <span>{t('riskRegister.bell.pending', { count: bell.count })}</span>
          </UnstyledButton>
        </li>
      ) : null}
      {authorizations > 0 && onOpenAuthorizations ? (
        <li>
          <UnstyledButton
            className={documents.bellItem}
            data-counted
            onClick={onOpenAuthorizations}
          >
            <span className={documents.bellIcon} aria-hidden>
              <IconAlertTriangle size={16} />
            </span>
            <span>{t('riskDashboard.bell.authorizations', { count: authorizations })}</span>
          </UnstyledButton>
        </li>
      ) : null}
    </ul>
  );
}

/** Register'ın yolu (zil maddesi). */
export const RA_REGISTER_PATH = '/compliance/risk-assessment/register';
